import { PIXEL_CULLING_THRESHOLD, USE_WEBGPU } from "../wgs/globals";
import { FrustumIntersector } from '../wgs/scene/FrustumIntersector';
import { RenderContext } from "../wgs/render/RenderContext";
import { getGlobal } from "../compat";
import { RenderBatch } from "../wgs/scene/RenderBatch";
import { MeshFlags } from "../wgs/scene/MeshFlags";
import { SceneMath } from "../wgs/scene/SceneMath";
import { RenderFlags } from "../wgs/scene/RenderFlags";
import THREE from "three";
import * as et from "./EventTypes";
import SheetRenderContext from "../wgs/render/SheetRenderContext";
import { Renderer } from '../wgs/wgpu/Renderer';
import { RenderContextWebGPU } from '../wgs/wgpu/RenderContextWebGPU';
import { GetDefaultWebGPUDevice } from "../wgs/wgpu/globals.js";
import { MaterialManager } from "../wgs/render/MaterialManager";
import { analytics } from '../analytics/index';
import html2canvas from 'html2canvas';

/** @import { UnifiedCamera } from "../tools/UnifiedCamera"; */
/** @import { Viewer3DImpl } from "./Viewer3DImpl"; */

/**
 * Old screenshot function that is stil the public Viewer3D.getScreenShot API.
 * It resizes the HTML canvas element and captures that.
 * we use Blob URL, Chrome crashes when opening dataURL that is too large
 * https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
 *
 * @private
 */
function getScreenShotLegacy(viewer, w, h, cb, overlayRenderer) {
  var viewerImpl = viewer.impl;
  var renderContext = viewerImpl.renderer();
  var oldWidth = renderContext.settings.logicalWidth;
  var oldHeight = renderContext.settings.logicalHeight;
  var oldProgressiveRender = viewerImpl.progressiveRender;
  var pixelRatio = viewerImpl.glrenderer().getPixelRatio();

  if (!w || !h) {
    w = oldWidth;
    h = oldHeight;
  }

  viewerImpl.progressiveRender = false;

  // Render the scene with new size
  viewerImpl.resize(w / pixelRatio, h / pixelRatio, true);

  viewerImpl.tick(performance.now());
  let finalTarget;
  let webGpuCanvas;
  if (USE_WEBGPU) {
    webGpuCanvas = renderContext.createWebGpuCanvas(w, h);
    finalTarget = webGpuCanvas.texture;
  } else {
    // Use an offscreen target to render the overlays
    finalTarget = new THREE.WebGLRenderTarget(w, h, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBFormat,
      type: THREE.UnsignedByteType
    });
  }

  const offscreenTargetBackup = renderContext.getOffscreenTarget();
  renderContext.setOffscreenTarget(finalTarget);
  // Rebuild the final output into the offscreen buffer.
  renderContext.presentBuffer();
  // Turn off offscreen target
  renderContext.setOffscreenTarget(offscreenTargetBackup);

  function renderToCanvas() {

    let res;
    if (USE_WEBGPU) {
      // The result is already rendered to the canvas we created.
      res = webGpuCanvas;
    } else {
      res = renderContext.targetToCanvas(finalTarget || renderContext.getColorTarget());
      // Dispose of the offscreen target, if there is one
      if (finalTarget) {
        finalTarget.dispose();
        finalTarget = undefined;

        // Make sure that we don't leave a deleted target assigned in WebGLRenderer.
        // This avoids a WebGL warning "WebGL: INVALID_OPERATION: bindFramebuffer: attempt to use a deleted object"
        // in WebGlRenderer.initFrameBuffer later. Note that this warning would happen even if no one actually renders to this target.
        viewerImpl.glrenderer().setRenderTarget(null);
      }
    }

    /**
     * @param {Object} result
     * @param {HTMLCanvasElement} result.canvas
     * @param {function()|undefined} result.cleanup
     */
    function finalizeRender(result) {
      // Resize the render context back the original size
      viewerImpl.progressiveRender = oldProgressiveRender;
      viewerImpl.resize(oldWidth, oldHeight, true);

      result.canvas.toBlob(function (blob) {
        var newBlobURL = getGlobal().URL.createObjectURL(blob);
        if (cb) {
          cb(newBlobURL, blob);
        } else {
          getGlobal().open(newBlobURL);
        }
        // This will be present on webgpu canvas results.
        if (result.cleanup) {
          result.cleanup();
        }
      }, 'image/png');
    }

    if (overlayRenderer) {
      // TODO: This won't work in WebGPU because it expects a 2d canvas
      // context. We could copy the WebGPU canvas into a 2d canvas to get
      // it to work with an overlayRenderer.
      if (USE_WEBGPU) {
        console.warn(
          'getScreenShotLegacy() in WebGPU mode does not currently support overlayRenderer param.');
      }
      overlayRenderer(res.ctx, () => {finalizeRender(res);});
    } else {
      finalizeRender(res);
    }
  }

  // In case the model is leaflet, we should wait until all the tiles will be downloaded in the new scale.
  // Otherwise, the screenshot will be pixelated.
  if (viewer.model.isLeaflet()) {

    var cancelLeafletScreenshot = false;

    const cancelCB = function cancelCB() {
      cancelLeafletScreenshot = true;
      viewerImpl.progressiveRender = oldProgressiveRender;
      viewerImpl.resize(oldWidth, oldHeight, true);
      // Dispose of the offscreen target, if there is one
      if (finalTarget)
      finalTarget.dispose();
    };

    viewer.addEventListener(et.CANCEL_LEAFLET_SCREENSHOT, cancelCB, { once: true });

    // invalidate is needed here in order to make sure that the iterator's reset method will be called.
    viewerImpl.invalidate(true);
    viewer.model.getIterator().callWhenRefined(function () {
      if (!cancelLeafletScreenshot) {
        viewer.removeEventListener(et.CANCEL_LEAFLET_SCREENSHOT, cancelCB);
        renderToCanvas();
      }
    });
  } else {
    renderToCanvas();
  }
}


// Creates a blob containing a snapshot and passes result to onFinished.
// It runs in the background parallel to main rendering.
//
//  @param {function(result)} onFinished - Gets called with the resulting image as both a Blob URL and Blob, i.e. onFinished(blobUrl, blob).
//                                         If options.returnAsTarget is true, it is called with (renderContext, renderTarget, camera, sheetRenderer, extraTargets) instead.
//  @param {UnifiedCamera}    [options.camera]       - If undefined, we use a copy of the current main camera
//  @param {function(number)} [options.onProgress]   - progress callback. Gets progress value as integer
//  @param {RenderContext}    [options.renderContext]- optional render context to use for the screenshot rendering
//  @param {SheetRenderContext}[options.sheetRenderer]- optional sheet render context to use for screenshot rendering
//  @param {Object}           [options.renderConfig] - custom RenderContext configuration (by default, we use the config of the main renderer)
//  @param {function()}       [options.beforeRender] - triggered before rendering into the screenshot. This allows to apply temporary changes
//                                                     (e.g. on materials) that would affect main rendering otherwise.
//  @param {function()}       [options.afterRender]  - triggered after rendering into snapshot. Use this to revert/cleanup any changes done in beforeRender.
//  @param {av.Model[]}       [options.models]       - Array of RenderModels to appear in the screenshot. (Default: all currently visible ones)
//  @param {Object}           [options.fragOptions]  - By default, visibility and ghosting of fragments are 1:1 inherited from main viewer.
//                                                     Optionally, fragOptions allow for customizing fragment states, e.g. to force everything to be visible.
//                                                     Return undefined to preserve original fragment state. Available options:
//                                                         fragOptions.isFragVisible: see FragmentList.setVisibility {function(fragId)=>bool}
//                                                         fragOptions.isFragOff:     see FragmentList.setFragOff    {function(fragId)=>bool}
//  @param {boolean}            [options.excludeCutPlanes] - By default, cutplanes are inherited from main view. This flag makes the screenshot independent
//                                                         of any active cutplanes.
//  @param {boolean}            [options.excludeThemingColors] - By default (false), theming colors are inherited from main view.
//                                                             Note: This option only supports 3D so far.
//  @param {boolean}            [options.is2d]             - By default, we decide dimensionality from main viewer.
//  @param {boolean}            [options.dontWaitForLeafletTiles] - By default, we wait until all the leaflet tiles are being fully loaded before we complete the render.
//                                                                If set to true, we just use the low res tiles if the high res tiles don't exist yet.
//  @param {boolean}            [options.renderOverlayScenes] - Render overlay scenes in final step to overlayTarget. If options.overlayScenes is not provided, renders all overlay scenes.
//  @param {Object}             [options.overlayScenes]       - An object containing overlay scenes to render to overlayTarget
//. @param {boolean}            [options.disableOverlayBlend] - Prevent blending overlays onto the final target.
//  @param {Array}              [options.extraTargets]   - Extra named targets to return in onFinished callback
//  @param {Element[]}          [options.includeHTMLElements] - An array of HTML elements to include in the screenshot. Only HTML elements that are currently visible in the DOM and are children of the viewer will be captured.
//  @param {Viewer3DImpl}     viewerImpl             - main viewer instance - to get glRenderer, material manager, default models etc.
//  @returns {Object} Control object C: Use...
//                    - C.stop() to cancel computation
//                    - C.finished() to check if it's done
//
// Known limitations:
//  - When using custom render settings: AO can only be used if the main canvas uses it as well. This is because materials are shared and must be compiled differently based
//                                       whether a depth target is used (as for AO)
const getScreenShot = (w, h, onFinished, options, viewerImpl) => {
  /** @type {MaterialManager} */
  let materials = viewerImpl.getMaterials();
  let renderer = viewerImpl.glrenderer();
  let renderContext = viewerImpl.renderer();
  let modelQueue = viewerImpl.modelQueue();

  options = options || {};

  // get scenes
  let scenes = [];
  let models = options.models || modelQueue.getModels();

  // Create a wrapper for HTML elements that should be included in the screenshot so we can copy them to not be impacted by navigational changes
  const shouldCaptureHTML = options?.includeHTMLElements && options.includeHTMLElements.length > 0;
  // if HTML elements are specified, we will clone them now so future navigation does not affect the screenshot.
  const htmlElementWrapper = shouldCaptureHTML ? cloneHTMLElementsToWrapperElement(options.includeHTMLElements, viewerImpl) : null;

  for (let i = 0; i < models.length; i++) {
    let model = models[i];
    let iter = model.getIterator();

    // skip models that don't support getGeomScenes (e.g. leaflets)
    // Leaflet scenes are handled separately below.
    if (!iter || !iter.getGeomScenes) {
      continue;
    }

    // Collect all scenes from next model
    let modelScenes = iter.getGeomScenes();
    for (let j = 0; j < modelScenes.length; j++) {
      // Some scenes may not exist. E.g., if it corresponds to an empty BVH node.
      let scene = modelScenes[j];
      if (scene) {
        scenes.push(scene);
      }
    }
  }

  let camera = options.camera || viewerImpl.camera;

  // Sort scene by depth to make sure transparent objects are rendered correctly
  // Note: this is not sufficient to guarantee that all transparent objects are rendered correctly, since geom scenes can intersect
  scenes.sort((a, b) => {
    let sortA = a.sortObjects ?? true;
    let sortB = b.sortObjects ?? true;
    if (sortA !== sortB) {
      return sortA ? 1 : -1;
    }
    if (sortA) {
      return camera.getWorldDirection().dot(a.getBoundingBox().getCenter()) - camera.getWorldDirection().dot(b.getBoundingBox().getCenter());
    } else {
      return 0;
    }
  });

  // Make sure that we never use the same camera object as the main canvas. Otherwise, tools may modify the camera and affect the
  // screenshot during rendering.
  if (camera === viewerImpl.camera) {
    camera = camera.clone();
  }

  // Make sure all matrices are updated.
  if (camera.isUnifiedCamera) {
    camera.updateCameraMatrices();
  }

  // Make sure the camera client width/height is set according to screenshot custom size
  camera.clientWidth = w;
  camera.clientHeight = h;

  // Screenshots may contain objects that are culled in the main canvas.
  // Therefore, we need this apply correct culling for RenderBatches.
  const frustum = new FrustumIntersector();
  const cutplanes = options.excludeCutPlanes ? [] : materials.getCutPlanes();
  const cutplanesHideInterior = options.excludeCutplanes ? false : materials.cutplanesHideInterior > 0;
  frustum.reset(camera, cutplanes, cutplanesHideInterior);
  frustum.areaCullThreshold = PIXEL_CULLING_THRESHOLD;

  if (USE_WEBGPU && !options.renderContext) {
    // If a renderContext is passed in, we will reuse its Renderer.
    // Otherwise we will create a new Renderer.

    // For WebGPU, we cannot use the same renderer for offscreen rendering,
    // because the WebGPURenderer owns the render targets. So we create our
    // own just for the screenshot.
    const device = GetDefaultWebGPUDevice();
    const params = { pixelRatio: 1.0 };
    renderer = new Renderer(device, params);

    // Safety net: clamp to the GPU's max texture size to avoid render target
    // creation failures. Callers should clamp before calling getScreenShot.
    const maxDim = device.limits.maxTextureDimension2D;
    if (w > maxDim || h > maxDim) {
      console.warn(`getScreenShot: requested ${w}x${h} exceeds maxTextureDimension2D (${maxDim}), reducing resolution`);
      const scale = Math.min(maxDim / w, maxDim / h);
      w = Math.floor(w * scale);
      h = Math.floor(h * scale);
      camera.clientWidth = w;
      camera.clientHeight = h;
    }

    // For WebGPU, the irradiance map is set manually in Viewer3DImpl.loadIrradianceMap
    // So, we must forward it to the new renderer as well.
    const mainIbl = viewerImpl.glrenderer().getIBL();
    renderer.getIBL().copyFrom(mainIbl);

    // Disable RenderBundles for screenshots. Why?
    //  - Not critically needed if we just use a single image
    //  - RenderBundles are attached to RenderBatches, so they would
    //    conflict with main renderer when using the same model.
    renderer.getMainPass().disableRenderBundles();
  }

  setupViewport();

  let ctx;

  // create temp RenderContext
  if (options.renderContext) {
    ctx = options.renderContext;
    if (USE_WEBGPU) {
      // WebGPU must use the render context's Renderer.
      renderer = ctx.getRenderer();
    }
  } else {
    if (USE_WEBGPU) {
      ctx = new RenderContextWebGPU();
      ctx.init(renderer, w, h);
    } else {
      ctx = new RenderContext();
      ctx.init(renderer, w, h, { offscreen: true });
    }
  }

  // WebGPURenderer needs to know all 3D models.
  if (USE_WEBGPU) {
    for (const model of models) {
      // Note: If renderer is newly created, we always add the models. However, the renderer may also come
      //       from outside and already have models if it's passed via options.renderContext.
      if (model.is3d() && !renderer.has3dModel(model)) {
        renderer.add3dModel(model);
      }
    }
  }

  let sheetRenderer;
  if (viewerImpl.isSheetRendererNeeded()) {
    if (options.sheetRenderer) {
      sheetRenderer = options.sheetRenderer;
    } else {
      sheetRenderer = new SheetRenderContext(viewerImpl, ctx, renderer, materials);
    }
  }

  // For WebGL, it is needed to add the depth and edge materials as override materials to the RenderContext
  // to make sure that cutplanes are considered for depth and edge rendering.
  // The WebGPURenderContext has no separate depth and edge materials and doesn't need this.
  if (!USE_WEBGPU) {
    // Make sure that cutplanes are consistently applied to override materials of our new RenderContext.
    // Note: We are replacing these override materials on each screenshot call. This will not work anymore
    //       if anyone runs multiple screenshots in parallel (which would work otherwise).
    //       For this, we would need to use unique ids here and always clean-up the materials afterwards.
    materials.addOverrideMaterial("normalsMaterial_screenshot", ctx.getDepthMaterial());
    materials.addOverrideMaterial("edgeMaterial_screenshot", ctx.getEdgeMaterial());
  }

  // by default, configure RenderContext in the same way as for main rendering (edges, cubemap etc.)
  let cfg = options.renderConfig || renderContext.getConfig();
  ctx.applyConfig(cfg);

  // Render scenes
  const lights = viewerImpl.lightsOn ? viewerImpl.lights : viewerImpl.no_lights;
  ctx.beginScene(viewerImpl.scene, camera, lights, true);

  restoreViewport();

  let MaxTimePerCycle = 20; // Time in ms that we allow per cycle
  let sceneIndex = 0; // Next scene to be rendered

  // Allow safe interruptions from outside
  let reqId = undefined;
  let control = {
    stop: () => {
      if (!reqId) {
        return;
      }
      // cancel timeout
      getGlobal().clearTimeout(reqId);

      // if there are leaflets, tell them that there is no need anymore to
      // refine them for the screenshot.
      releaseLeafLetViews();
    },
    finished: () => !!reqId
  };

  let lastProgress = -1;

  // Leaflets: Register separate views to make sure that the tiles are loaded without affecting the main view
  let leaflets = [];
  for (let i = 0; i < models.length; i++) {
    let model = models[i];
    if (model.isLeaflet()) {
      let iter = model.getIterator();
      leaflets.push({
        iter: iter,
        viewId: iter.registerView(),
        ready: false // fully refined and ready for rendering
      });
    }
  }

  function setupViewport() {
    // In WebGPU we have a separate Renderer and don't have to care about
    // preserving and recovering previous state.
    if (USE_WEBGPU) {
      return;
    }

    renderer.pushViewport();
    // Preserve pixelRatio based on browser device pixel size.
    // For offline rendering, we use 1:1 to get exactly the desired resolution.
    renderer.setPixelRatio(1.0);
    // Please note that pixelratio is also used to define the viewport.
    renderer.setViewport(0, 0, w, h);
  }

  function restoreViewport() {
    // In WebGPU we have a separate Renderer and don't have to care about
    // preserving and recovering previous state.
    if (USE_WEBGPU) {
      return;
    }

    renderer.popViewport();
    // recover original pixel ratio
    renderer.setPixelRatio();
  }

  // Continue refinement of leaflets for this screenshot. Returns true when done.
  const continueLeafletRefine = () => {
    let allReady = true;
    for (let i = 0; i < leaflets.length; i++) {
      let leaflet = leaflets[i];
      if (!leaflet.ready) {
        leaflet.ready = leaflet.iter.reset(frustum, camera, leaflet.viewId);
      }
      allReady = allReady && leaflet.ready;
    }

    allReady = allReady || options.dontWaitForLeafletTiles;

    // Once leaflets are ready, collect the scenes to be rendered
    if (allReady) {
      for (let i = 0; i < leaflets.length; i++) {
        let leaflet = leaflets[i];
        let scene = leaflet.iter.getScene(leaflet.viewId);
        scenes.push(scene);
      }
    }

    // When done, collect
    return allReady;
  };

  // When leaflet rendering is done, unregister view so that the leaflet iterator
  // knows that the tiles are allowed to be deleted in cache cleanups later.
  const releaseLeafLetViews = () => {
    // Release tiles needs for leaflets
    leaflets.forEach((l) => l.iter.unregisterView(l.viewId));
    leaflets.length = 0;
  };

  // Used to remember fragment states per scene if we use overrides
  let fragOptions = options.fragOptions;
  let visFlags = fragOptions ? [] : undefined;

  // Optional: Remember fragment states and overwrite with custom ones
  const applyFragmentOverrides = (scene) => {

    // Note: Since we work on unconsolidated geometry, we assume here that Fragments are
    //       always rendered using RenderBatches. Without that, things would get a bit more
    //       complicated here.
    let useOverrides = !!fragOptions && scene instanceof RenderBatch;
    if (!useOverrides) {
      return;
    }

    let fragList = scene.frags;
    let overrideFragState = function (fragId, index) {

      // remember original fragment flags per scene
      visFlags[index] = fragList.vizflags[fragId];

      // We set the flags directly here without affecting allVisibleDirty,
      // because we recover the orginal state before returning to main render.

      // Customize isFragOff flag
      let isOff = fragOptions.isFragOff && fragOptions.isFragOff(fragId);
      if (isOff !== undefined) {
        fragList.setFlagFragment(fragId, MeshFlags.MESH_HIDE, isOff);
      }

      // Customize isVisible
      let isVisible = fragOptions.isFragVisible && fragOptions.isFragVisible(fragId);
      if (isVisible !== undefined) {
        fragList.setFlagFragment(fragId, MeshFlags.MESH_VISIBLE, isVisible);
      }
    };
    scene.forEachNoMesh(overrideFragState);
  };

  // Restore original state for all fragments of a RenderBatch
  const revertFragmentOverrides = (scene) => {

    let useOverrides = !!fragOptions && scene instanceof RenderBatch;
    if (!useOverrides) {
      return;
    }

    let fragList = scene.frags;
    let recoverFragState = function (fragId, index) {
      let flags = visFlags[index];
      let wasOff = !!(flags & MeshFlags.MESH_HIDE);
      let wasVisible = !!(flags & MeshFlags.MESH_VISIBLE);

      fragList.setFlagFragment(fragId, MeshFlags.MESH_HIDE, wasOff);
      fragList.setFlagFragment(fragId, MeshFlags.MESH_VISIBLE, wasVisible);
    };
    scene.forEachNoMesh(recoverFragState);
  };

  function setPixelScale(pixelsPerUnit, width, height, camera) {
    // The WebGPU Renderer sets this value globally.
    if (USE_WEBGPU) {
      renderer.updatePixelScale(pixelsPerUnit, camera);
    }
    materials.updatePixelScale(pixelsPerUnit, width, height, camera);
  }

  // Determine pixelPerUnit parameter for this screenshot
  const getPixelsPerUnitValue = () => {

    // Get bounds from viewer - unless we have custom models
    let worldBox = undefined;
    if (!options.models) {
      worldBox = viewerImpl.getVisibleBounds();
    } else {
      // If we have custom models, we use the summed model bounds.
      // Note: Strictly speaking, we would need to consider individual fragment visibility/overrides here too.
      //       But this would make things even more complicated here and only might make a difference in edge cases.
      worldBox = new THREE.Box3();
      for (let i = 0; i < models.length; i++) {
        let model = models[i];
        worldBox.union(model.getBoundingBox());
      }
    }

    // initialized after ctx.beginScene() - which also sets up the render targets on first call.
    let is2d = Object.prototype.hasOwnProperty.call(options, 'is2d') ? options.is2d : viewerImpl.is2d;

    // It is important that we call it after ctx.beginScene(), so that these values are initialized.
    let deviceHeight = ctx.settings.deviceHeight;

    // Consider cutplane if wanted
    let cutPlanes = options.excludeCutPlanes ? undefined : materials.getCutPlanesRaw();
    let cutPlane = cutPlanes && cutPlanes[0];

    // For 2d: If we have a sheet model box, its base elevation is considered as reference plane for 2d rendering.
    let modelBox = models[0] && models[0].getBoundingBox();

    // Note that getPixelsPerUnit() uses the _deviceHeight of the RenderContext. This value is only
    return SceneMath.getPixelsPerUnit(camera, is2d, worldBox, deviceHeight, cutPlane, modelBox);
  };
  const pixelsPerUnit = getPixelsPerUnitValue();

  // Decouple theming from main rendering
  let EmptyThemingColors = []; // temporarily used empty theming-color table
  let themingColors = null; // temporarily used to backup original theming colors
  const disableThemingColors = (scene) => {

    if (!options.excludeThemingColors || !(scene instanceof RenderBatch)) {
      return;
    }

    // This option is only supported for 3d atm. For 2d, we would need some
    // extra work because theming-colors are stored per vertex.
    if (scene.frags.is2d) {
      return;
    }

    themingColors = scene.frags.db2ThemingColor;
    scene.frags.db2ThemingColor = EmptyThemingColors;
  };
  const restoreThemingColors = (scene) => {
    if (!themingColors) {
      return;
    }
    scene.frags.db2ThemingColor = themingColors;
    themingColors = null;
  };

  // Note: Changing the number of cutplanes on each render cycle would trigger repeated shader recompiles (see MaterialManager.setCutPlanes)
  //       Changing the cutplane values is much cheaper and just changes a shader param.
  //       => To temporarily nuke a cutplane, we just change it to only exclude shapes in outer space
  let dummyCutPlane = new THREE.Vector4(0, 0, -1, -1e+20);
  let dummyCutPlanes = [];
  let originalCutPlanes = null;

  const disableCutPlanes = () => {
    // Note that the cutplanes in main view may change at any time.
    // So we have to get them again when an update cycle starts.
    originalCutPlanes = materials.getCutPlanes();

    // make sure that dummyCutPlanes has the same length
    let planeCount = originalCutPlanes.length;
    if (dummyCutPlanes.length !== planeCount) {
      dummyCutPlanes.length = planeCount;
      for (let i = 0; i < planeCount; i++) {
        dummyCutPlanes[i] = dummyCutPlane;
      }
    }
    renderContext.toggleTwoSided(materials.setCutPlanes(dummyCutPlanes));
  };

  const recoverCutPlanes = () => {
    renderContext.toggleTwoSided(materials.setCutPlanes(originalCutPlanes));
  };

  const onRenderDone = () => {

    releaseLeafLetViews();

    if (options.renderOverlayScenes) {
      const overlayScenes = options.overlayScenes ?? viewerImpl.overlayScenes;
      let lights = viewerImpl.lightsOn ? viewerImpl.lights : viewerImpl.no_lights;
      ctx.renderOverlays(overlayScenes, lights);
    }

    // In the simplest case, we can just read-back the color-target directly.
    let finalTarget = ctx.getColorTarget();
    let webGpuCanvas;

    // If we need post-processing (SAO/FXAA/blending overlays) we must run
    // presentBuffer to render into an offscreen target.
    let needsPostProcess =
    cfg.aoEnabled || cfg.antialias || options.renderOverlayScenes;
    // But if we don't want to blend the overlays onto the final target then
    // we must skip any post processing.
    let needsPresentBuffer = !options.disableOverlayBlend && needsPostProcess;
    if (needsPresentBuffer) {
      if (cfg.aoEnabled) {
        ctx.computeSSAO();
      }

      if (USE_WEBGPU) {
        // Create a WebGPU canvas to directly render the final pass into.
        webGpuCanvas = ctx.createWebGpuCanvas(w, h);
        finalTarget = webGpuCanvas.texture;
      } else {
        // Use an offscreen target to render the overlays
        finalTarget = new THREE.WebGLRenderTarget(w, h, {
          minFilter: THREE.LinearFilter,
          magFilter: THREE.LinearFilter,
          format: THREE.RGBAFormat,
          type: THREE.UnsignedByteType
        });
      }

      ctx.setOffscreenTarget(finalTarget);
      ctx.presentBuffer();
    }

    if (options.returnAsTarget) {
      // We can not render HTML elements in this mode, so we don't support includeHTMLElements.
      if (options.includeHTMLElements && options.includeHTMLElements.length) {
        console.warn('getScreenShot() with returnAsTarget does not support includeHTMLElements option.');
      }
      const extraTargets = options.extraTargets ? options.extraTargets.map((target) => ctx.getNamedTarget(target)) : undefined;
      onFinished(ctx, finalTarget, camera, sheetRenderer, extraTargets);
      return;
    }

    if (!options.sheetRenderer) {// Destroy only if it was created (and it's not being returned)
      sheetRenderer?.destroy();
      sheetRenderer = null;
    }

    if (webGpuCanvas) {
      finishScreenshot(webGpuCanvas);
    } else {
      const res = ctx.targetToCanvas(finalTarget);
      finishScreenshot(res);
    }
  };

  /**
   * Read result into Blob.
   * @param {Object | Promise<Object>} result
   * @param {HTMLCanvasElement} result.canvas
   * @param {function()|undefined} result.cleanup
   */
  const finishScreenshot = async (result) => {
    // if we got our result from a webgpu context we need to wait for it to be ready
    // since await creates a fulfilled promise if it is called on a non-promise object we will immediately continue if there was no promise
    result = await result;

    if (htmlElementWrapper) {
      let ctx = result.ctx;
      if (USE_WEBGPU) {
        // since we can't draw an image directly onto a webgpu canvas we need to pass its content to a 2d canvas
        const canvas2d = document.createElement('canvas');
        [canvas2d.width, canvas2d.height] = [result.canvas.width, result.canvas.height];
        ctx = canvas2d.getContext('2d');
        ctx.drawImage(result.canvas, 0, 0);
        result.canvas = canvas2d;
      } else {
        // since the WebGL render context is flipped (see RenderContext.targetToCanvas), we need to flip it back before drawing on the unflipped extra canvas
        ctx.scale(1, -1);
        ctx.translate(0, -h);
      }

      document.body.appendChild(htmlElementWrapper);
      try {
        await drawHTMLElementToContext(w, h, ctx, htmlElementWrapper);
      } catch (e) {
        console.error('Error while drawing HTML elements to screenshot canvas:', e);
      }
      document.body.removeChild(htmlElementWrapper);
    }

    result.canvas.toBlob(function (blob) {
      let newBlobURL = getGlobal().URL.createObjectURL(blob);
      if (onFinished) {
        onFinished(newBlobURL, blob);
        // clean up render context
        if (!options.renderContext) {
          ctx.cleanup();
        }
      }
      if (result.cleanup) {
        result.cleanup();
      }
    });
  };

  const continueRender = () => {
    // Leaflets may need to be refined first
    let waitForLeaflets = !continueLeafletRefine();
    if (waitForLeaflets) {
      reqId = getGlobal().setTimeout(continueRender, 0);
      return;
    }

    // Render scenes until time is up or all scenes are done
    let tStart = performance.now();

    setupViewport();

    let pixelsPerUnitBackup = viewerImpl.getPixelsPerUnit(viewerImpl.camera, viewerImpl.getVisibleBounds());
    setPixelScale(pixelsPerUnit, w, h, camera);

    // Disable cutplanes if not wanted in the screenshot
    if (options.excludeCutPlanes) {
      disableCutPlanes();
    } else {
      // In case that LeechViewer is being used, and the target models are being shared between viewers with different section state,
      // there might be scenarios where the cut planes inside the materials are not synced with the current viewer state (See LeechViewer.restoreViewerState).
      // By calling syncCutPlanes, it makes sure that the current cutplanes inside the materials are the correct ones for the active viewer.
      viewerImpl.api.syncCutPlanes?.();
    }

    // Allow client to do custom modifcations without affecting main rendering
    options.beforeRender && options.beforeRender();

    while (sceneIndex < scenes.length) {
      let scene = scenes[sceneIndex];

      // In WebGPU the screenshot Renderer does not currently share
      // uniforms with the main Renderer, so we need to make sure they get
      // updated.
      if (USE_WEBGPU) {
        scene.uniformsNeedUpdate = true;
      }

      // Find the current model that we are going to render.
      const model = models.find((m) => m.id === scene.frags?.modelId);

      if (model?.is2d()) {
        const transform = model.getModelToViewerTransform();
        const scaling = transform ? transform.getMaxScaleOnAxis() : 1;

        // Set pixelsPerUnit according to each sheet in 3D space. In general, it is set according to the modelQueue's bounds
        // which is not related to how we want to present the sheet (i.e. the line thickness will vary when selecting a
        // floor as a result, because of the changing viewing volume)
        // Note: Previously this was done only when in 3D mode, but it's also needed in 2D in case a transform with
        // scaling is set.
        if (!viewerImpl.is2d || scaling !== 1) {
          const bounds = model.getVisibleBounds();
          const deviceWidth = ctx.settings.deviceWidth;
          const deviceHeight = ctx.settings.deviceHeight;
          // Sending is2d:true here because we want the calculation path done for 2D sheets
          const pixelsPerUnit = SceneMath.getPixelsPerUnit(camera, true, bounds, deviceHeight, null, bounds);
          // The WebGPU Renderer sets this value globally.
          if (USE_WEBGPU) {
            renderer.updatePixelScale(pixelsPerUnit, camera);
          }

          materials.updatePixelScaleForModel(model, pixelsPerUnit, deviceWidth, deviceHeight, scaling, camera);
        }
      }

      // If specified, apply custom values for fragment states (ghosting, fragOff)
      // Note that these flags are not evaluated in renderScenePart, but earlier
      // in applyVisibility() already.
      applyFragmentOverrides(scene);

      disableThemingColors(scene);
      // Make sure that culling is applied based on screenshot camera. Without this code, some parts would be
      // missing if they are currently not visible in the main canvas.
      if (scene instanceof RenderBatch) {
        scene.applyVisibility(RenderFlags.RENDER_NORMAL, frustum);
      }

      if (sheetRenderer && scene.frags.is2d) {
        sheetRenderer.renderScenePart(scene, true, true, false, ctx);
      } else {
        ctx.renderScenePart(scene, true, true, false, false);

        // Render ghosted objects.
        if (scene.frags && !scene.frags.areAllVisible() && viewerImpl.showGhosting || fragOptions) {
          if (scene instanceof RenderBatch) {
            scene.applyVisibility(RenderFlags.RENDER_HIDDEN, frustum);
          }

          viewerImpl.prepareEdgesForGhostedPass(true);
          scene.overrideMaterial = viewerImpl.fadeMaterial;
          const prevEdgesOnly = scene.edgesOnly;
          scene.edgesOnly = prevEdgesOnly || !scene.frags.is2d && renderContext.isGhostedEdgesOnly();

          ctx.renderScenePart(scene, true, true, false, false);

          scene.edgesOnly = prevEdgesOnly;
          scene.overrideMaterial = null;
          viewerImpl.prepareEdgesForGhostedPass(false);
        }
      }

      restoreThemingColors(scene);

      revertFragmentOverrides(scene);

      sceneIndex++;

      // stop loop if time is up
      let elapsed = performance.now() - tStart;
      if (elapsed > MaxTimePerCycle) {
        break;
      }
    }

    if (USE_WEBGPU) {
      // Ensure all GPUCommandBuffers are submitted.
      renderer.flushCommandQueue();
      if (sheetRenderer) {
        sheetRenderer.glRenderer.flushCommandQueue();
      }
    }

    restoreViewport();
    setPixelScale(pixelsPerUnitBackup, renderContext.settings.deviceWidth, renderContext.settings.deviceHeight, camera);

    // Recover cutplanes for main view rendering
    if (options.excludeCutPlanes) {
      recoverCutPlanes();
    }

    // Allow client to remove custom modifications before main canvas renders again
    options.afterRender && options.afterRender();

    // If we are done...
    if (sceneIndex === scenes.length) {
      // ...start image readback
      onRenderDone();
    } else {
      // Otherwise, let a bit time for main rendering and continue later.
      reqId = getGlobal().setTimeout(continueRender, 0);
    }

    // track progress
    if (options.onProgress) {
      let percent = Math.floor(100 * sceneIndex / scenes.length);
      if (percent !== lastProgress) {
        lastProgress = percent;
        options.onProgress(percent);
      }
    }
  };

  continueRender();

  return control;
};

/**
 * Helper method to create a wrapper HTML element that contains all HTML elements specified. CSS styles are preserved.
 * The wrapper element is sized to match the viewer's canvas size, and the elements are positioned relative to the viewer's canvas.
 * This is useful for capturing HTML elements in a screenshot using html2canvas.
 * @param {Array} elementsToClone - Array of HTML elements to clone and capture
 * @param {Viewer3DImpl} viewerImpl - Viewer instance to get the original canvas size and bounding rect
 * @return {HTMLElement} - Returns a wrapper element containing the cloned HTML elements, styled and positioned correctly.
 */
const cloneHTMLElementsToWrapperElement = (elementsToClone, viewerImpl) => {
  // get the original canvas size from the viewer so our wrapper can be sized correctly
  const canvasBounds = viewerImpl.getCanvasBoundingClientRect();
  const { width, height } = canvasBounds;

  // Get the device pixel ratio to account for display scaling
  const devicePixelRatio = viewerImpl.glrenderer().getPixelRatio() || window.devicePixelRatio || 1;

  // Set some style properties by hand
  // This is so the cloned elements can be positioned correctly in the new wrapper
  const wrapper = document.createElement('div');
  wrapper.style.position = 'absolute';
  // Scale wrapper dimensions to match the actual screenshot pixel dimensions
  wrapper.style.width = `${width * devicePixelRatio}px`;
  wrapper.style.height = `${height * devicePixelRatio}px`;
  wrapper.style.overflow = 'hidden';
  wrapper.style.zIndex = '-99999'; // Try to keep it hidden even when adding to the DOM
  // wrapper.style.visibility = 'hidden'; // Hide during DOM manipulation

  // Properties that html2canvas doesn't support and actively make the screenshot worse if included
  const excludedProperties = ['box-shadow'];

  //  Helper method to compile a string of all given styles except the ones that html2canvas doesn't support so we can apply them later
  const copyComputedStyleIntoString = (element) => {
    let styleString = '';
    const style = window.getComputedStyle(element);
    if (style.length > 0) {
      for (let i = 0; i < style.length; i++) {
        const property = style.item(i);
        if (!excludedProperties.includes(property)) {
          const value = style.getPropertyValue(property);
          styleString += `${property}: ${value}; `;
        }
      }
    }
    return styleString;
  };

  const viewerContainer = document.querySelector('.adsk-viewing-viewer');
  if (!viewerContainer) {
    return wrapper; // If no viewer container is found, return an empty wrapper
  }

  // Deep clone each element with full style preservation
  for (const element of elementsToClone) {
    const rect = element.getBoundingClientRect();

    // Skip elements that are not part of the viewer component as those should not be included in the screenshot
    // Also skip elements that are not effectively visible by checking their dimensions
    const inViewer = viewerContainer.contains(element);
    const isVisible = element.offsetWidth > 0 && element.offsetHeight > 0;
    if (!inViewer || !isVisible) {
      continue;
    }

    // Create a deep clone that includes all child nodes and copy all styles
    const clone = element.cloneNode(true);
    let originalStyleString = copyComputedStyleIntoString(element);
    clone.style.cssText = originalStyleString;

    // Calculate position relative to the viewer canvas
    // Account for device pixel ratio to handle display scaling correctly
    const offsetLeft = (rect.left - canvasBounds.left) * devicePixelRatio;
    const offsetTop = (rect.top - canvasBounds.top) * devicePixelRatio;

    // Override positioning to place correctly in wrapper (relative coordinates)
    clone.style.position = 'absolute';
    clone.style.left = `${offsetLeft}px`;
    clone.style.top = `${offsetTop}px`;
    clone.style.transform = `scale(${devicePixelRatio})`;
    clone.style.transformOrigin = 'top left';

    // Recursively apply computed styles to all child elements (excluding unsupported props)
    const applyStylesToChildren = (originalParent, clonedParent) => {
      const originalChildren = originalParent.children;
      const clonedChildren = clonedParent.children;

      for (let i = 0; i < originalChildren.length && i < clonedChildren.length; i++) {
        const originalChild = originalChildren[i];
        const clonedChild = clonedChildren[i];
        let childStyleString = copyComputedStyleIntoString(originalChild);
        clonedChild.style.cssText = childStyleString;

        if (originalChild.children.length > 0) {
          applyStylesToChildren(originalChild, clonedChild);
        }
      }
    };

    applyStylesToChildren(element, clone);

    wrapper.appendChild(clone);
  }

  return wrapper;
};

async function drawHTMLElementToContext(w, h, ctx, elementToDraw) {
  const canvas = await html2canvas(elementToDraw, { backgroundColor: null, width: ctx.canvas.width, height: ctx.canvas.height });
  ctx.drawImage(canvas, 0, 0, w, h);
}

/**
 * Creates a screenshot of the viewer, with extra parameters to enable more control.
 * Common uses are for creating screenshot with an overlay, specific bounds and crop.
 * The output image returns as blob, inside onDone callback.
 *
 * @param {Viewer3D} [viewer] - Viewer instance
 * @param {Number} [width] - Width of the screenshot, before cropping.
 * @param {Number} [height] - Height of the screenshot, before cropping.
 * @param {Function} [onDone] - A callback called when the screenshot is ready. Signature: onDone(blobUrl, outputWidth, outputHeight);
 * @param {Object} [options] - Additional initialization options. Not mandatory.
 * @param {Boolean} [options.fullPage] - For 2D documents only. Output will be a cropped image of the viewer, without the grey background.
 * @param {THREE.Box3} [options.bounds] - Bounds in world coordinates of the screenshot. The virtual camera of the screenshot will zoom in to these bounds.
 * @param {Function} [options.getCropBounds] - A callback used to get crop bounds of the image. Signature: getCropBounds(viewer, camera, bounds).
 * @param {Number} [options.margin] - Extra margin over the bounds.
 * @param {Function} [options.overlayRenderer] - a callback used to render an overlay on top of the viewer. Signature: overlayRenderer(viewer, opt, overlayRendererExtraOptions)
 * @param {Object} [options.overlayRendererExtraOptions] - Extra options object used only by the overlayRenderer.
 *
 * @private
 */
const getScreenShotWithBounds = (viewer, width, height, onDone, options) => {
  var {
    fullPage, bounds, getCropBounds, margin, overlayRenderer, overlayRendererExtraOptions
  } = options;

  if (USE_WEBGPU && overlayRenderer) {
    // TODO: This could be complicated to get working.
    // This is used in at least these extensions:
    //  - Autodesk.BIM360.Markups
    //  - Autodesk.BIM360.Extension.PushPin
    // They appear to have a lot of assumptions about being in WebGL
    // mode, and being able to render to custom targets.
    console.warn(
      'getScreenShotWithBounds() in WebGPU mode does not currently support overlayRenderer option.');
  }

  if (fullPage) {
    bounds = viewer.impl.getVisibleBounds();
  }

  const canvasBounds = viewer.impl.getCanvasBoundingClientRect();
  const originalWidth = canvasBounds.width;

  let camera = viewer.navigation.getCamera();

  if (bounds) {
    camera = getCameraWithFitBounds(viewer, bounds, margin);
  }

  const sceneBounds = getSceneClientBounds(viewer, camera);

  try {
    // Scale the viewer so the full render width won't contain the gray canvas layout.
    if (fullPage) {
      const sceneBoundsSize = sceneBounds.getSize(new THREE.Vector3());
      const ratio = originalWidth / Math.max(sceneBoundsSize.x, sceneBoundsSize.y);
      width = Math.floor(width * ratio);
      height = Math.floor(height * ratio);
    }

    // Reduce resolution if it exceeds the GPU's max texture dimension.
    if (USE_WEBGPU) {
      // Since USE_WEBGPU is true and we survived until here, we can safely assume
      // the device to exist.
      const maxDim = GetDefaultWebGPUDevice().limits.maxTextureDimension2D;
      if (maxDim && (width > maxDim || height > maxDim)) {
        const scale = Math.min(maxDim / width, maxDim / height);
        analytics.track('viewer.screenshot.resolution_clamped', {
          requested_width: width, requested_height: height,
          clamped_width: Math.floor(width * scale), clamped_height: Math.floor(height * scale),
          max_texture_dimension: maxDim
        });
        width = Math.floor(width * scale);
        height = Math.floor(height * scale);
      }
    }

    // This callback is used for cropping the image.
    const cropScreenshot = (canvas) => {
      // In case of a 2D full page, crop the view so the image will be without the gray margin.
      if (fullPage) {
        const scaleRatio = width / originalWidth;

        sceneBounds.min.x *= scaleRatio;
        sceneBounds.min.y *= scaleRatio;
        sceneBounds.max.x *= scaleRatio;
        sceneBounds.max.y *= scaleRatio;

        cropImage(canvas, sceneBounds, onDone);
        // Crop to a specific bounding box
      } else if (getCropBounds) {
        const clientBounds = getCropBounds(viewer, camera, bounds);
        cropImage(canvas, clientBounds, onDone);
        // Don't crop anything, just call onDone
      } else {
        canvas.toBlob((blob) => {
          const blobUrl = getGlobal().URL.createObjectURL(blob);
          onDone(blobUrl, width, height);
        });
      }
    };

    // Deselect everything before taking the screenshot.
    const selected = viewer.impl.selector.getSelection();
    viewer.clearSelection();

    return getScreenShot(width, height, (ctx, target, screenshotCamera) => {
      viewer.impl.selector.setSelection(selected);

      if (overlayRenderer) {
        const opt = {
          width,
          height,
          ctx,
          target,
          screenshotCamera,
          onRenderDone: cropScreenshot
        };
        overlayRenderer(viewer, opt, overlayRendererExtraOptions);
      } else if (USE_WEBGPU) {
        // For WebGPU, targetToCanvas is async
        ctx.targetToCanvas(target).then(({ canvas, cleanup }) => {
          cropScreenshot(canvas);
          cleanup();
        });
      } else {
        const { canvas } = ctx.targetToCanvas(target);
        cropScreenshot(canvas);
      }
    }, { returnAsTarget: true, camera }, viewer.impl);
  } catch (error) {
    console.error('getScreenShot error: ' + error);
    onDone(null);
  }
};

/**
 * Creates a screenshot of the viewer, in original screen resolution.
 * Common uses are for creating thumbnails.
 * The output image returns as base64 image, inside callback.
 *
 * @param {Viewer3D} [viewer] - Viewer instance
 * @param {Function} [callback] - A callback called when the screenshot is ready. Signature: callback(base64Image);
 * @param {Object} [options] - Optional - Additional initialization options. See getScreenShot Documentation for more details.
 *
 * @private
 */
const getScreenShotAtScreenSize = (viewer, callback, options) => {
  const canvasBounds = viewer.impl.getCanvasBoundingClientRect();
  const width = Math.floor(canvasBounds.width);
  const height = Math.floor(canvasBounds.height);

  const onDone = (blob, outputWidth, outputHeight) => {
    if (!blob) {
      callback(null);
      return;
    }

    blobToImage(blob, outputWidth, outputHeight, (img) => {
      callback(img, outputWidth, outputHeight);
    });
  };

  getScreenShotWithBounds(viewer, width, height, onDone, options);
};

/**
 * Returns the scene's client bounding box. In case of F2D, it will return the bounds without the gray area.
 * @private
 */
const getSceneClientBounds = (viewer, camera) => {
  const worldBounds = viewer.impl.getVisibleBounds(undefined, undefined, undefined, true); // exclude shadows

  const min = viewer.worldToClient(worldBounds.min, camera);
  const max = viewer.worldToClient(worldBounds.max, camera);

  const bbox = new THREE.Box3().setFromPoints([min, max]);

  return bbox;
};

// Crops an image according to given bounds.
// Output as blobUrl.
const cropImage = (canvas, bounds, callback) => {
  const _document = getGlobal().document;
  const tmpCanvas = _document.createElement('canvas');
  const tmpCtx = tmpCanvas.getContext('2d');
  const size = bounds.getSize(new THREE.Vector3());
  const imageWidth = Math.min(Math.floor(size.x), canvas.width);
  const imageHeight = Math.min(Math.floor(size.y), canvas.height);
  tmpCanvas.width = imageWidth;
  tmpCanvas.height = imageHeight;

  const cropFromX = Math.max(Math.floor(bounds.min.x), 0);
  const cropFromY = Math.max(Math.floor(bounds.min.y), 0);
  const cropToX = imageWidth;
  const cropToY = imageHeight;
  tmpCtx.drawImage(canvas, cropFromX, cropFromY, cropToX, cropToY, 0, 0, imageWidth, imageHeight);

  tmpCanvas.toBlob((blob) => {
    var newBlobURL = getGlobal().URL.createObjectURL(blob);
    callback(newBlobURL, imageWidth, imageHeight);
  }, 'image/png');
};

// Converts blobUrl to base64 image.
const blobToImage = (blobUrl, width, height, callback) => {
  const _document = getGlobal().document;
  const tmpCanvas = _document.createElement('canvas');
  const tmpCtx = tmpCanvas.getContext('2d');
  const img = new Image();

  tmpCanvas.width = width;
  tmpCanvas.height = height;
  img.src = blobUrl;

  img.onload = function () {
    tmpCtx.drawImage(img, 0, 0, width, height);
    const outputImg = tmpCanvas.toDataURL('image/png');
    callback(outputImg);
  };
};

// Converts given bounds object to square bounds.
const makeBoundsSquare = (bounds, minValue = 0) => {
  const size = bounds.getSize(new THREE.Vector3());
  const maxSize = Math.max(size.x, size.y, minValue);
  bounds.expandByVector(new THREE.Vector3((maxSize - size.x) / 2, (maxSize - size.y) / 2, 0));
};

// Get camera with fit bounds.
const getCameraWithFitBounds = (viewer, bounds, margin) => {
  margin = margin || viewer.navigation.FIT_TO_VIEW_HORIZONTAL_MARGIN;

  const marginBackup = {
    horizontal: viewer.navigation.FIT_TO_VIEW_HORIZONTAL_MARGIN,
    vertical: viewer.navigation.FIT_TO_VIEW_VERTICAL_MARGIN
  };

  // Change global bounds margins.
  viewer.navigation.FIT_TO_VIEW_HORIZONTAL_MARGIN = margin;
  viewer.navigation.FIT_TO_VIEW_VERTICAL_MARGIN = margin;

  // Get a clone of the camera in a fit to view position
  const camState = viewer.getState({ viewport: true });
  viewer.navigation.fitBounds(true, bounds, false, true);

  // Clone virtual camera
  const camera = viewer.navigation.getCamera().clone();

  // Restore global bound margins & camera.
  viewer.restoreState(camState, undefined, true);
  viewer.navigation.FIT_TO_VIEW_HORIZONTAL_MARGIN = marginBackup.horizontal;
  viewer.navigation.FIT_TO_VIEW_VERTICAL_MARGIN = marginBackup.vertical;

  // Update camera's near-far values
  viewer.impl.updateNearFarValues(camera, bounds);

  return camera;
};


/**
 * Contains static functions for capturing screenshots from the viewer.
 * @namespace Autodesk.Viewing.ScreenShot
 */
export let ScreenShot = {
  getScreenShotLegacy,
  getScreenShot,
  cloneHTMLElementsToWrapperElement,
  drawHTMLElementToContext,
  getScreenShotWithBounds,
  getScreenShotAtScreenSize,
  getSceneClientBounds,
  cropImage,
  blobToImage,
  makeBoundsSquare,
  getCameraWithFitBounds
};